import React, { useEffect } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { motion, AnimatePresence } from "framer-motion"
import CancelIcon from "components/atoms/_icons/CancelIcon"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { AppTheme } from "types/general.types"
import { PremiumOnboardingSlide } from "types/premium.types"
import { useRandomIndex } from "hooks/useRandomIndex"
import { useAppSelector } from "appRedux/hooks"
import {
  updateHasSeenPremiumBanner,
  updateLastSeenPremiumBanner,
  updatePremiumCurrentOnboardingSlide,
  updateShowPremiumOnboarding
} from "appRedux/slices/premiumSlice"
import Logo from "components/atoms/_icons/Logo"

export default function ProBanner() {
  const hasSeenPremiumBanner = useAppSelector((state) => state.premium.hasSeenPremiumBanner)
  const lastSeenPremiumBanner = useAppSelector((state) => state.premium.lastSeenPremiumBanner)
  const PREMIUM_SLIDES_FOR_ACTIVE_FEATURES = useAppSelector((state) => state.premium.premiumSlidesForActiveFeatures)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const premiumSlideIndex = useRandomIndex(PREMIUM_SLIDES_FOR_ACTIVE_FEATURES.length, lastSeenPremiumBanner) as PremiumOnboardingSlide

  useEffect(() => {
    dispatch(updateLastSeenPremiumBanner(premiumSlideIndex))
  }, [premiumSlideIndex])

  function handleOnBannerClicked() {
    dispatch(updateShowPremiumOnboarding(true))
    dispatch(updatePremiumCurrentOnboardingSlide(premiumSlideIndex as PremiumOnboardingSlide))
  }

  function handleCloseBanner(event: React.MouseEvent<HTMLDivElement>) {
    event.stopPropagation()
    dispatch(updateHasSeenPremiumBanner(true))
  }

  return (
    <AnimatePresence>
      {!hasSeenPremiumBanner && (
        <motion.div
          initial={{ opacity: 0, height: "174px" }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          style={{ overflow: "hidden" }}
        >
          <Container mode={PREMIUM_SLIDES_FOR_ACTIVE_FEATURES[premiumSlideIndex].theme} onClick={handleOnBannerClicked}>
            <BackgroundVectors>
              <TopVectors mode={PREMIUM_SLIDES_FOR_ACTIVE_FEATURES[premiumSlideIndex].theme}/>
              <BottomVectors mode={PREMIUM_SLIDES_FOR_ACTIVE_FEATURES[premiumSlideIndex].theme}/>
            </BackgroundVectors>
            <Wrapper>
              <CloseIconWrapper mode={PREMIUM_SLIDES_FOR_ACTIVE_FEATURES[premiumSlideIndex].theme} onClick={handleCloseBanner}>
                <CancelIcon/>
              </CloseIconWrapper>
              <ContentContainer mode={PREMIUM_SLIDES_FOR_ACTIVE_FEATURES[premiumSlideIndex].theme}>
                <Logo/>
                <p className="title">{t(`premium.slides.${PREMIUM_SLIDES_FOR_ACTIVE_FEATURES[premiumSlideIndex].key.toLowerCase()}.title`)}</p>
              </ContentContainer>
            </Wrapper>
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

type ContainerProps = {
  theme?: Theme;
  mode: AppTheme;
}

const Container = styled(Stack)(({ theme, mode }: ContainerProps) => ({
  width: "100%",
  maxWidth: "var(--max-container-width)",
  height: "166px",
  backgroundColor: mode === "light" ? theme?.palette.primary["1"] : theme?.palette.primary["8"],
  overflow: "hidden",
  borderRadius: "8px",
  marginBottom: "8px",
  position: "relative",

  "& svg#logo": {
    width: "50px !important",
    height: "50px !important",

    "& path": {
      fill: mode === "light" ? theme?.palette.primary["6"] : theme?.palette.gray["1"]
    }
  }
}))

const BackgroundVectors = styled("div")(() => ({
  position: "absolute",
  top: 0,
  right: 0,
  width: "100%",
  height: "100%"
}))

const TopVectors = styled("div")(({ theme, mode }: ContainerProps) => ({
  width: "100%",
  height: "100%",
  position: "absolute",
  top: 0,
  right: 0,

  "&::before": {
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    right: 0,
    backgroundImage: mode === "light" ? "url('/static/images/abstract-vectors/wave-vector-light.svg')" : "url('/static/images/abstract-vectors/wave-vector-dark.svg')",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "top left",
    zIndex: 1
  }
}))

const BottomVectors = styled("div")(({ theme, mode }: ContainerProps) => ({
  width: "100%",
  height: "100%",
  position: "absolute",
  bottom: 0,
  left: 0,

  "&::before": {
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
    bottom: 0,
    right: 0,
    backgroundImage: mode === "light" ? "url('/static/images/abstract-vectors/wave-vector-light.svg')" : "url('/static/images/abstract-vectors/wave-vector-dark.svg')",
    backgroundRepeat: "no-repeat",
    transform: "rotate(180deg)",
    backgroundPosition: "bottom left",
    zIndex: 1
  }
}))

const Wrapper = styled(Stack)(() => ({
  height: "100%",
  zIndex: 10
}))

const CloseIconWrapper = styled(Stack)(({ theme, mode }: ContainerProps) => ({
  width: "40px",
  height: "40px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",
  backgroundColor: mode === "light" ? theme?.palette.primary["4"] : theme?.palette.primary["7"],
  marginRight: "auto",
  position: "absolute",
  top: "10px",
  left: "10px",

  "& svg path": {
    fill: "#FFFFFF !important"
  }
}))

const ContentContainer = styled(Stack)(({ theme, mode }: ContainerProps) => ({
  width: "100%",
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  direction: "ltr",

  "& img": {
    maxWidth: "calc(var(--max-container-width) * 0.72)",

    "@media (max-height: 740px)": {
      maxWidth: "calc(var(--max-container-width) * 0.5)"
    }
  },

  "& p": {
    justifyContent: "center",
    alignItems: "center",
    color: mode === "light" ? theme?.palette.primary["7"] : "#FFFFFF",
    textAlign: "center",
    fontSize: "1.6rem",
    fontWeight: 700
  }
}))
